/**
 * TanStack Router Configuration
 *
 * Sets up file-based routing with typed context for auth and query client.
 * Uses beforeLoad for protected route guards.
 */

import type { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';

import type { AuthContextValue } from '@/providers/auth-provider';

// Import the generated route tree (created by TanStack Router plugin)
import { routeTree } from './routeTree.gen';

/**
 * Router context type
 *
 * Available in all route loaders and beforeLoad hooks
 */
interface RouterContext {
  auth: AuthContextValue;
  queryClient: QueryClient;
}

/**
 * Creates the app router with context
 *
 * @param context - Auth and query client context
 * @returns Configured router instance
 */
export function createAppRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
  });
}

// Type registration for TanStack Router
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
